import { gql, useMutation } from "@apollo/client";
import {
  RedeemOfferMutationMutation,
  RedeemOfferMutationMutationVariables
} from "../../../schemaTypes";

export const redeemOfferMutation = gql`
  mutation RedeemOfferMutation(
    $id: String!
    $referralCode: String
  ) {
    redeemOffer(
      id: $id
      referralCode: $referralCode
    ) {
      errors {
        path
        message
      }
      message
      njoyCode
      partnerVoucher
      referralUrl
    }
  }
`;

type RedeemOfferPayload = NonNullable<RedeemOfferMutationMutation["redeemOffer"]> & {
  partnerVoucher?: string | null;
};

export interface RedeemOfferResult {
  errors: Array<{ path: string | null | undefined; message: string }> | null;
  message: string | null;
  njoyCode?: string | null;
  partnerVoucher?: string | null;
  referralUrl?: string | null;
  supportMeta?: {
    code?: string;
    supportRef?: string;
  };
}

export function useRedeemOffer() {
  const [mutate, { data, loading, error }] = useMutation<
    RedeemOfferMutationMutation,
    RedeemOfferMutationMutationVariables
  >(redeemOfferMutation);

  // Normalized handler for frontend usage
  const redeemOffer = async (
    variables: RedeemOfferMutationMutationVariables
  ): Promise<RedeemOfferResult> => {
    try {
      const result = await mutate({ variables });
      const payload = result.data?.redeemOffer as RedeemOfferPayload | undefined;

      const normalizedErrors = (payload?.errors || [])
        .filter((err): err is NonNullable<typeof err> => Boolean(err))
        .map((err) => ({
          path: err.path,
          message: err.message,
        }));

      return {
        errors: normalizedErrors.length > 0 ? normalizedErrors : null,
        message: payload?.message || null,
        njoyCode: payload?.njoyCode,
        partnerVoucher: payload?.partnerVoucher,
        referralUrl: payload?.referralUrl,
        supportMeta: undefined,
      };
    } catch (err) {
      const maybeApolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{ message?: string; extensions?: { code?: string; supportRef?: string } }>;
      };

      const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
      const message =
        firstGraphQLError?.message ||
        maybeApolloErr.message ||
        "Unable to redeem offer right now. Please try again.";

      return {
        errors: [{ path: "general", message }],
        message: null,
        njoyCode: undefined,
        partnerVoucher: undefined,
        referralUrl: undefined,
        supportMeta: {
          ...(typeof firstGraphQLError?.extensions?.code === "string"
            ? { code: firstGraphQLError.extensions.code }
            : {}),
          ...(typeof firstGraphQLError?.extensions?.supportRef === "string"
            ? { supportRef: firstGraphQLError.extensions.supportRef }
            : {}),
        },
      };
    }
  };

  return { redeemOffer, data, loading, error };
}